// ApprovalPage — handles email-based approval links.
// Users arrive here via email links: /t/:slug/approve?token=aprvl:...
// The page validates the token, displays what's being approved, and
// provides a Confirm button to consume the token.
//
// States:
//   Loading  — fetching token details
//   Valid    — shows approval details + confirm button
//   Error    — token invalid/expired/consumed/stale
//   Consumed — success confirmation

import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { approvalsApi } from '@/lib/api/approvals'
import { Button } from '@/components/ui/button'
import { CheckCircle2, ShieldCheck, AlertTriangle, Clock, Loader2 } from 'lucide-react'
import { useSearch } from '@tanstack/react-router'

// --- Helper: human-readable labels ---

const purposeLabels: Record<string, string> = {
  confirmation_gate: 'Confirmation',
  secondary_signoff: 'Secondary Approval',
  action_closure: 'Action Closure Approval',
}

const subjectTypeLabels: Record<string, string> = {
  taxonomy_version: 'Taxonomy Version',
  likelihood_version: 'Likelihood Version',
  impact_table_version: 'Impact Table Version',
  action: 'Action',
}

function formatPurpose(purpose: string): string {
  return purposeLabels[purpose] || purpose
}

function formatSubjectType(subjectType: string): string {
  return subjectTypeLabels[subjectType] || subjectType
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

// --- Main component ---

export function ApprovalPage() {
  const { token } = useSearch({ strict: false }) as { token?: string }
  const api = useApi()
  const appApi = approvalsApi(api)
  const { slug } = useTenant()
  const [consumed, setConsumed] = useState(false)

  // Validate token on load
  const {
    data: approval,
    isLoading,
    error: validateError,
  } = useQuery({
    queryKey: ['tenant', slug, 'approval-validate', token],
    queryFn: () => appApi.validate(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity, // Don't re-fetch — token state is point-in-time
  })

  // Consume mutation
  const consumeMutation = useMutation({
    mutationFn: () => appApi.consume(token!),
    onSuccess: () => setConsumed(true),
  })

  // No token in URL
  if (!token) {
    return (
      <CenteredCard>
        <AlertTriangle className="h-10 w-10 text-destructive" />
        <h2 className="text-lg font-semibold">Missing Token</h2>
        <p className="text-sm text-muted-foreground">
          No approval token was provided. Please check the link in your email.
        </p>
      </CenteredCard>
    )
  }

  // Loading
  if (isLoading) {
    return (
      <CenteredCard>
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Validating approval token…</p>
      </CenteredCard>
    )
  }

  // Validation error
  if (validateError) {
    const errorMessage = getErrorMessage(validateError)
    return (
      <CenteredCard>
        <AlertTriangle className="h-10 w-10 text-destructive" />
        <h2 className="text-lg font-semibold">Approval Unavailable</h2>
        <p className="text-sm text-muted-foreground">{errorMessage}</p>
      </CenteredCard>
    )
  }

  // Consumed successfully
  if (consumed) {
    return (
      <CenteredCard>
        <CheckCircle2 className="h-10 w-10 text-green-600" />
        <h2 className="text-lg font-semibold">Approved</h2>
        <p className="text-sm text-muted-foreground">
          The {formatSubjectType(approval!.subject_type).toLowerCase()} has been approved successfully.
        </p>
      </CenteredCard>
    )
  }

  // Valid token — show details + confirm button
  if (approval) {
    return (
      <CenteredCard>
        <ShieldCheck className="h-10 w-10 text-primary" />
        <h2 className="text-lg font-semibold">{formatPurpose(approval.purpose)}</h2>

        <div className="w-full space-y-3 rounded-lg border bg-muted/30 p-4 text-sm">
          <DetailRow label="Type" value={formatSubjectType(approval.subject_type)} />
          <DetailRow label="Requested by" value={approval.requested_by_name} />
          <DetailRow label="Requested" value={formatDateTime(approval.requested_at)} />
          <DetailRow
            label="Expires"
            value={formatDateTime(approval.expires_at)}
            icon={<Clock className="inline h-3.5 w-3.5 text-muted-foreground" />}
          />
        </div>

        {consumeMutation.error && (
          <p className="text-sm text-destructive">
            {getErrorMessage(consumeMutation.error)}
          </p>
        )}

        <Button
          onClick={() => consumeMutation.mutate()}
          disabled={consumeMutation.isPending}
          className="w-full"
        >
          {consumeMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Confirming…
            </>
          ) : (
            'Confirm Approval'
          )}
        </Button>
      </CenteredCard>
    )
  }

  return null
}

// --- Layout helpers ---

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border bg-background p-8 shadow-sm">
        {children}
      </div>
    </div>
  )
}

function DetailRow({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon?: React.ReactNode
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">
        {icon && <span className="mr-1">{icon}</span>}
        {value}
      </span>
    </div>
  )
}

// --- Error message extraction ---

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 409) {
      return 'This item has been modified since the approval was requested. A new approval is needed.'
    }
    if (error.status === 404) {
      return 'This approval link is no longer valid. It may have expired, already been used, or been replaced by a newer request.'
    }
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred.'
}
